import {
  attachClosestEdge,
  extractClosestEdge
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { type Edge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/types';
import { DropIndicator } from '@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/box-without-terminal';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
  draggable,
  dropTargetForElements
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { token } from '@atlaskit/tokens';
import SelectFieldMenu, {
  FieldMenuSpotlightTarget,
  FieldMenuSpotlightTourProvider,
  type SelectFieldMenuHandle
} from '@databases/context-menu/select-field-menu';
import { useContextualMenu } from '@databases/contextual-menu/useContextualMenu';
import {
  AiSummaryFieldHeaderAction,
  hasAiSummaryHeaderRefresh,
  useIsFieldScopedAiStreaming
} from '@databases/database-ai-fields';
import {
  hasNoEmptyAiSummaryEntries,
  hasUserEditedAiSummaryCells,
  runManualAiSummaryRefresh
} from '@databases/database-ai-fields/run-ai-summary-field';
import { useIsAiFieldRunActive } from '@databases/database-ai-fields/use-is-ai-field-run-active';
import {
  aiFirstCreationStreamingState,
  isBackendAgentStreamingParticipant
} from '@databases/database-ai-first-creation-state';
import {
  BORDER_BLUE,
  BORDER_GREEN,
  BORDER_ORANGE,
  BORDER_PURPLE,
  useDatabaseStreamState
} from '@databases/database-ai-first-creation-ui';
import { DragType, setPublicDragData } from '@databases/drag';
import FieldNameWithActions, {
  type FieldNameWithActionsProps
} from '@databases/field-name/FieldNameWithActions';
import Z_INDEX from '@databases/layout/z-index';
import { useDatabaseSelectionPing } from '@databases/ping-registry';
import {
  getPinnedLeadingFieldCountAfterFieldsInserted,
  getPinnedLeadingFieldCountAfterFieldsRemoved
} from '@databases/pinned-columns/compute-pinned-leading-layout';
import { usePinnedLeadingColumns } from '@databases/pinned-columns/pinned-leading-columns-context';
import {
  getPinnedLeadingHeaderCellStyle,
  isPinnedLeadingDropIndicatorOccluded,
  PINNED_LEADING_BOUNDARY_DROP_TARGET_ACTIVE_STYLE,
  PINNED_LEADING_BOUNDARY_DROP_TARGET_INACTIVE_STYLE,
  PINNED_LEADING_BOUNDARY_SELECTOR,
  PINNED_LEADING_DROP_INDICATOR_WIDTH_PX
} from '@databases/pinned-columns/pinned-leading-header';
import {
  getPinnedLeadingBoundaryCellShadowStyle,
  getPinnedLeadingHeaderSurfaceGapCoverStyle
} from '@databases/pinned-columns/pinned-leading-shadow';
import { setPinnedLeadingFieldCountInProperties } from '@databases/pinned-columns/pinned-leading-view-properties';
import { PresenceNameplate, usePresence } from '@databases/presence';
import { isModifierKeyPressed, useHoverState } from '@databases/react';
import { getService } from '@databases/service-container';
import { type Database } from '@databases/types/database';
import { TABLE_CLASSNAMES } from '@databases/utils/constants';
import { blurActiveElement, removeTextSelection } from '@databases/utils/database/utils';
import {
  type ViewChangeType,
  ViewChangeTypes
} from '@databases/utils/database/view/view';
import { useIsFeatureEnabled } from '@databases/utils/hooks/useIsFeatureEnabled';
import { legacyColors as colors } from '@databases/utils/legacy-colors';
import useView from '@databases/views/useView';
import composeRefs from '@seznam/compose-react-refs';
import classNames from 'classnames';
import React, {
  type CSSProperties,
  type ForwardedRef,
  forwardRef,
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useIntl } from 'react-intl-next';
import styled, { css } from 'styled-components';
import { useSnapshot } from 'valtio';

import { getExperimentValue } from '@canvas/client-feature-flags';
import { manuallyFireExposureEvent } from '@canvas/client-feature-flags/experiments';

import { isApple } from '../../../utils.ts';
import { useDatabaseAiConfigModal } from '../ai-config/database-ai-config-modal-context.ts';
import { useEditAiSummaryField } from '../ai-config/use-edit-ai-summary-field.ts';

import { buildFieldDragImageFromState } from './build-field-drag-image-from-state.tsx';
import { getAiFieldStreamingParticipants } from './get-ai-field-streaming-participants.ts';
import { type DragState } from './TableLayoutRow.tsx';

type AiSummaryRerunOperation = 'refresh-entire-column' | 'refresh-empty-entries';

const HEADER_INTERACTIVE_TARGET_SELECTOR = [
  '[data-contextual-menu="true"]',
  '.menu-portal-target',
  '[role="listbox"]',
  '[role="option"]',
  ':is(button, input, textarea, select, a, [contenteditable="true"])'
].join(', ');

const DropIndicatorStyling = css<{ containerHeight: number }>`
  ::after {
    content: ' ';
    position: absolute;
    opacity: 1;
    top: ${token('space.negative.050', '-4px')};
    left: ${token('space.negative.025', '-2px')};
    width: ${PINNED_LEADING_DROP_INDICATOR_WIDTH_PX}px;
    height: ${({ containerHeight }) => `${containerHeight - 8}px`};
    border-radius: ${token('radius.xsmall', '2px')};
    border: none;
    background-color: ${colors.B200};
    transition: opacity 0.2s;
    z-index: ${Z_INDEX.TABLE_VIEW_DROP_INDICATOR};
  }
`;

const PragmaticDropIndicatorStyling = styled.div<{ containerHeight: number }>`
  content: ' ';
  position: absolute;
  opacity: 1;
  left: ${token('space.negative.025', '-2px')};
  height: ${({ containerHeight }) => `${containerHeight - 8}px`};
  border-radius: ${token('radius.xsmall', '2px')};
  border: none;
  background-color: ${colors.B200};
  transition: opacity 0.2s;
  z-index: ${Z_INDEX.TABLE_VIEW_DROP_INDICATOR};
`;

// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
const PragmaticDropIndicatorStylingLastInRow = styled.div<{ containerHeight: number }>`
  ::after {
    content: ' ';
    position: absolute;
    opacity: 1;
    top: ${token('space.0', '0px')};
    width: 2px;
    right: -1px;
    height: ${({ containerHeight }) => `${containerHeight - 8}px`};
    border-radius: ${token('radius.xsmall', '2px')};
    border: none;
    background-color: ${token('color.border.selected', '#0C66E4')};
    transition: opacity 0.2s;
    z-index: ${Z_INDEX.TABLE_VIEW_DROP_INDICATOR};
  }
`;

export const Th = styled.th<{
  isDragOver: boolean;
  containerHeight: number;
  isFirstInRow: boolean;
  isSelectedFromPing?: boolean;
  isFirstOfSelection?: boolean;
  isLastOfSelection?: boolean;
  collaboratorColor?: string;
}>`
  position: relative;
  overflow: visible;

  ${({ isSelectedFromPing, isFirstOfSelection, isLastOfSelection, isFirstInRow }) =>
    isSelectedFromPing &&
    css`
      &::after {
        content: '';
        position: absolute;
        // Extend left by 1px on non-first-in-row columns so the ::after aligns with the
        // body highlight overlay which measures from the outer border-box edge (including
        // the cell's 1px left border) via getBoundingClientRect().
        inset: 0 0 -1px ${isFirstInRow ? '0px' : '-1px'};
        background: color-mix(
          in srgb,
          ${token('color.background.accent.blue.subtlest', '#e9f2ff')} 25%,
          transparent
        );
        box-sizing: border-box;
        border-top: 2px solid ${token('color.border.selected', '#0c66e4')};
        border-bottom: none;
        border-left: ${
          isFirstOfSelection
            ? `2px solid ${token('color.border.selected', '#0c66e4')}`
            : 'none'
        };
        border-right: ${
          isLastOfSelection
            ? `2px solid ${token('color.border.selected', '#0c66e4')}`
            : 'none'
        };
        border-radius: ${isFirstOfSelection ? token('radius.small', '3px') : '0'}
          ${isLastOfSelection ? token('radius.small', '3px') : '0'} 0 0;
        pointer-events: none;
        z-index: 1;
      }

      .highlight-delete-intent &::after {
        background: color-mix(
          in srgb,
          ${token('color.background.danger.hovered', '#ffd5d2')} 25%,
          transparent
        );
        border-top-color: ${token('color.border.danger', '#e34935')};
        border-left-color: ${token('color.border.danger', '#e34935')};
        border-right-color: ${token('color.border.danger', '#e34935')};
      }
    `}
  --database-rovo-gradient-angle: 0deg;
  --column-height: 0px;
  --column-left-inset: 0px;
  &.${TABLE_CLASSNAMES.SHOW_AS_DISABLED} {
    * {
      cursor: ${({ isFilterOrSortingActive }) =>
        isFilterOrSortingActive ? 'not-allowed' : 'grabbing'} !important;
    }
  }

  background: ${token('elevation.surface.sunken')};

  &:hover:not(.${TABLE_CLASSNAMES.SHOW_AS_DISABLED}) {
    .${TABLE_CLASSNAMES.SELECT_BUTTON} {
      display: flex;
    }
  }

  ${({ isDragOver }) => isDragOver && DropIndicatorStyling}
  // This maximizes the table header cell height to the height of the whole table body to be able to drag the column over the whole table
  &.${TABLE_CLASSNAMES.MAXIMIZE_HEIGHT} {
    ::before {
      content: '';
      position: absolute;
      left: 0;
      top: ${token('space.negative.100', '-8px')};
      height: ${({ containerHeight }) => `${containerHeight - 8}px`};
      width: 100%;
    }
  }

  &.${TABLE_CLASSNAMES.IS_ROVO_GRADIENT_SELECTED}::before {
    content: '';
    position: absolute;
    top: ${token('space.negative.025', '-2px')};
    left: ${token('space.negative.025', '-2px')};
    right: ${token('space.negative.025', '-2px')};
    height: var(--column-height, 0px);
    border-radius: ${token('radius.small', '3px')};
    padding: ${token('space.025', '2px')};
    background: conic-gradient(
      from var(--database-rovo-gradient-angle, 0deg),
      ${BORDER_ORANGE} 25%,
      ${BORDER_GREEN} 25% 50%,
      ${BORDER_BLUE} 50% 75%,
      ${BORDER_PURPLE} 75% 100%
    );
    -webkit-mask:
      linear-gradient(#000 0 0) content-box,
      linear-gradient(#000 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
    z-index: 1;
  }

  ${({ collaboratorColor }) => {
    // eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
    const borderColor = collaboratorColor && token(collaboratorColor);

    return (
      borderColor &&
      css`
        border: ${token('border.width.selected', '2px')} solid ${borderColor};
        border-radius: ${token('radius.small', '3px')};
      `
    );
  }}
`;

type TableLayoutHeaderCellRefObj = RefObject<
  TableLayoutHeaderCellRefs<
    RefObject<HTMLDivElement | null>,
    RefObject<HTMLDivElement | null>
  >
>;

type TableLayoutHeaderCellRefs<H, T> = {
  headerFieldRef: H;
  tableBodyRef: T;
};

export interface TableLayoutHeaderCellProps extends FieldNameWithActionsProps {
  canEditFields: boolean;
  couldEditFields: boolean;
  className?: string;
  currentViewId: string;
  isLastInRow?: boolean;
  onDragStart?: (e: React.DragEvent, entryId: string) => void;
  onDrag?: (e: React.DragEvent, entryId: string) => void;
  onDragEnd?: (e: React.DragEvent, entryId: string) => void;
  isDraggingActive?: boolean;
  isDragOver: boolean;
  containerHeight: number;
  visibleTableContentHeight: number;
  fieldIndex: number;
  visibleFieldIds: ReadonlyArray<string>;
  fieldToScroll: string | null;
  setFieldToScroll: (fieldId: string | null) => void;
  /**
   * databasesFrontendHighlightScrollReliability control-arm switch: off, this component scrolls itself; on, TableLayoutHeaderRow does.
   */
  isScrollSettleFixEnabled: boolean;
}

/**
 * Renders a th element that contains the field name.
 * The field name can be dragged to reorder the fields.
 */
const TableLayoutHeaderCell = React.memo(
  forwardRef<
    TableLayoutHeaderCellRefs<
      ForwardedRef<HTMLElement | null>,
      ForwardedRef<HTMLTableSectionElement | null>
    >,
    TableLayoutHeaderCellProps
  >(
    (
      {
        canEditFields,
        contentId,
        fieldId,
        className,
        isLastInRow,
        onDragStart,
        onDrag,
        onDragEnd,
        isDraggingActive,
        containerHeight,
        visibleTableContentHeight,
        isDragOver,
        currentViewId,
        isFirstInRow,
        fieldIndex,
        visibleFieldIds,
        fieldToScroll,
        setFieldToScroll,
        isScrollSettleFixEnabled,
        ...otherProps
      },
      ref
    ) => {
      const { locale } = useIntl();
      const { view } = useView();
      const pinnedLeadingColumns = usePinnedLeadingColumns();
      const viewSettings = view.settings;
      const inputRef = useRef<HTMLInputElement>(null);
      const thRef = useRef<HTMLTableCellElement>(null);
      const pinnedLeadingBoundaryDropTargetRef = useRef<HTMLDivElement>(null);
      const pinnedLeadingBoundaryElementRef = useRef<HTMLElement | null>(null);
      const {
        enabled: isPinnedLeadingEnabled,
        layoutByFieldIndex,
        setPinnedLeadingFieldCount,
        pinnedLeadingFieldCount,
        maxPinnedLeadingFieldCount
      } = pinnedLeadingColumns;
      const pinnedLeadingLayout = isPinnedLeadingEnabled
        ? layoutByFieldIndex[fieldIndex]
        : undefined;
      const isPinnedLeadingFreezeBoundary = Boolean(
        pinnedLeadingLayout?.isFreezeBoundary
      );

      const { activeStream, isStreaming, operationType } = useDatabaseStreamState();
      const isDBAiFirstCreationEnabled = useIsFeatureEnabled('dbAiFirstCreation');
      const isEditStreaming = operationType === 'edit';
      const isFieldScopedAiStreaming = useIsFieldScopedAiStreaming(activeStream);
      const isAiFieldRunActive = useIsAiFieldRunActive({
        contentId,
        aiFieldType: 'summary',
        fieldId,
        activeStream
      });

      const { isSingleFieldStreaming } = useSnapshot(aiFirstCreationStreamingState);
      const isAIStreaming = isDBAiFirstCreationEnabled && isStreaming;
      const isFieldEditStreaming = isEditStreaming && Boolean(isSingleFieldStreaming);
      useEffect(() => {
        if (!isPinnedLeadingEnabled || pinnedLeadingFieldCount === 0) {
          pinnedLeadingBoundaryElementRef.current = null;
          return;
        }

        const boundaryElement = thRef.current?.ownerDocument.querySelector(
          PINNED_LEADING_BOUNDARY_SELECTOR
        );
        pinnedLeadingBoundaryElementRef.current =
          boundaryElement instanceof HTMLElement ? boundaryElement : null;
      }, [fieldIndex, isPinnedLeadingEnabled, pinnedLeadingFieldCount]);

      useEffect(() => {
        // Control arm: TableLayoutHeaderRow owns scrolling when this is on.
        if (isScrollSettleFixEnabled) {
          return;
        }

        if (isAIStreaming && !isEditStreaming) {
          setFieldToScroll(null);
          return;
        }

        if (isAIStreaming && isFieldEditStreaming) {
          setFieldToScroll(null);
          return;
        }

        if (fieldToScroll === fieldId && thRef?.current) {
          requestAnimationFrame(() => {
            thRef.current?.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'center'
            });
          });
        }
      }, [
        isScrollSettleFixEnabled,
        fieldId,
        fieldToScroll,
        setFieldToScroll,
        isEditStreaming,
        isFieldEditStreaming,
        isAIStreaming
      ]);

      const [isHoverDragButton, dragButtonHoverProps] = useHoverState();
      const isContextualMenusM2Enabled = getExperimentValue(
        'databasesContextualMenusM2',
        'enabled',
        {
          fireExperimentExposure: false
        }
      );

      const { participants, recentCollaboratorColor } = usePresence({
        grouping: { fieldId },
        componentId: 'TableLayoutHeaderCell',
        isEnabled: true
      });
      const displayParticipants = getAiFieldStreamingParticipants({
        participants,
        shouldExcludeAiAgent: isFieldScopedAiStreaming
      });
      const activeBackendAgentParticipant = displayParticipants.find(
        isBackendAgentStreamingParticipant
      );

      const isFieldSelectedFromPing = useDatabaseSelectionPing((reader) =>
        reader.isFieldSelected(fieldId)
      );

      const isFieldFirstOfSelection = useDatabaseSelectionPing(() =>
        getService('SelectionsApi').isFieldFirstOfRange(fieldIndex)
      );

      const isFieldLastOfSelection = useDatabaseSelectionPing(() =>
        getService('SelectionsApi').isFieldLastOfRange(fieldIndex)
      );

      const field = getService('DatabaseSdk').fields.getFieldById(fieldId);
      const aiSummaryField =
        field && hasAiSummaryHeaderRefresh(field.type) ? field : null;
      const {
        isAiSummaryRunActive: isLocalAiSummaryRunActive,
        runAiSummaryWithIndicator
      } = useDatabaseAiConfigModal();

      const createAiSummaryRerunHandler = useCallback(
        (operation: AiSummaryRerunOperation) => () => {
          if (!aiSummaryField) {
            return;
          }

          if (
            operation === 'refresh-empty-entries' &&
            hasNoEmptyAiSummaryEntries({ contentId, fieldId, viewId: currentViewId })
          ) {
            return;
          }

          const start = runAiSummaryWithIndicator({
            fieldId,
            run: (abortSignal) =>
              runManualAiSummaryRefresh({
                abortSignal,
                contentId,
                fieldId,
                targetFieldName: aiSummaryField.name,
                config:
                  aiSummaryField.settings.aiFieldConfig?.aiFieldType === 'summary'
                    ? aiSummaryField.settings.aiFieldConfig.field
                    : { outputFormat: 'paragraph' },
                locale,
                viewId: currentViewId,
                operation,
                triggerType: 'fullColumnRegenerate',
                runScope:
                  operation === 'refresh-empty-entries'
                    ? 'emptyCellsOnly'
                    : 'entireColumn',
                source: 'databaseView'
              })
          });
          if (start.kind === 'started') {
            void start.completion.catch(() => undefined);
          }
        },
        [
          aiSummaryField,
          contentId,
          currentViewId,
          fieldId,
          locale,
          runAiSummaryWithIndicator
        ]
      );

      const handleAiSummaryRerunEntireColumn = useMemo(
        () => createAiSummaryRerunHandler('refresh-entire-column'),
        [createAiSummaryRerunHandler]
      );
      const handleAiSummaryRerunEmptyEntries = useMemo(
        () => createAiSummaryRerunHandler('refresh-empty-entries'),
        [createAiSummaryRerunHandler]
      );
      const aiFieldLastRunAt = canEditFields
        ? aiSummaryField?.aiRunMetadata?.lastRunAt
        : undefined;

      const isSelected = isFieldSelectedFromPing;

      const getHasUserEditedAiSummaryCells = useCallback(() => {
        const database = getService('DatabaseSdk').toJSON() as Pick<
          Database,
          'entries' | 'entriesById'
        >;

        return hasUserEditedAiSummaryCells({ database, fieldId });
      }, [fieldId]);
      const selectionsApi = getService('SelectionsApi');
      const selectionsReader = getService('SelectionsReader');
      const fieldMenuRef = useRef<SelectFieldMenuHandle>(null);
      const [fieldMenuOpenEvent, setFieldMenuOpenEvent] = useState<
        { id: number; source: 'drag-handle' | 'field-header' } | undefined
      >();
      const handleEditAiSummaryField = useEditAiSummaryField({
        contentId,
        currentViewId,
        fieldId
      });

      /**
       * Modifier behavior must match field-name selection.
       *
       * - No modifier toggles a single field.
       * - Shift extends the range.
       * - Shift+Meta/Ctrl toggles an individual field.
       */
      const handleSelectColumn = useCallback(
        (e: React.MouseEvent | React.KeyboardEvent) => {
          const isMultiSelectOn = isModifierKeyPressed(e, [
            isApple ? 'metaKey' : 'ctrlKey',
            'shiftKey'
          ]);
          const isRangeSelectOn = isModifierKeyPressed(e, ['shiftKey']);
          blurActiveElement();
          removeTextSelection();

          if (isRangeSelectOn) {
            selectionsApi.applyFieldSelection({
              fieldId,
              gesture: isMultiSelectOn ? 'toggle' : 'extend'
            });
          } else {
            selectionsApi.applyFieldSelection({
              fieldId,
              gesture: 'replace-or-clear'
            });
          }

          selectionsApi.clearPreviewIntent();
        },
        [selectionsApi, fieldId]
      );

      const { closeMenu } = useContextualMenu();
      const isHeaderInteractiveTarget = useCallback((target: EventTarget | null) => {
        if (!(target instanceof Element)) {
          return false;
        }

        return target.closest(HEADER_INTERACTIVE_TARGET_SELECTOR) !== null;
      }, []);

      const fireContextualMenusM2Exposure = useCallback(() => {
        manuallyFireExposureEvent('databasesContextualMenusM2');
      }, []);

      const handleHeaderClickCapture = useCallback(
        (e: React.MouseEvent) => {
          if (!isContextualMenusM2Enabled || !canEditFields) {
            return;
          }

          if (isHeaderInteractiveTarget(e.target)) {
            return;
          }

          e.preventDefault();
          e.stopPropagation();
          fireContextualMenusM2Exposure();
          closeMenu();
          handleSelectColumn(e);
        },
        [
          canEditFields,
          closeMenu,
          fireContextualMenusM2Exposure,
          handleSelectColumn,
          isHeaderInteractiveTarget,
          isContextualMenusM2Enabled
        ]
      );

      const handleHeaderContextMenu = useCallback(
        (e: React.MouseEvent) => {
          if (!isContextualMenusM2Enabled || !canEditFields) {
            return;
          }

          if (isHeaderInteractiveTarget(e.target)) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }

          e.preventDefault();
          e.stopPropagation();

          setFieldMenuOpenEvent((event) => ({
            id: (event?.id ?? 0) + 1,
            source: 'field-header'
          }));
          void fieldMenuRef.current?.openAtPosition({ x: e.clientX, y: e.clientY });
        },
        [canEditFields, isContextualMenusM2Enabled, isHeaderInteractiveTarget]
      );

      const [dragState, setDragState] = useState<DragState>({ type: 'idle' });
      const showAsDisabled = isSelected && dragState.type === 'dragging';

      const dragImage = useRef<HTMLElement>();

      const handleDragStart = useCallback(
        (e: React.DragEvent) => {
          const headerCellElement = thRef.current;

          if (e.target !== e.currentTarget || !headerCellElement) {
            // Ignore dragging descendent elements
            return;
          }
          removeTextSelection();
          closeMenu();

          const selectedFieldIds = selectionsReader.getSelectedFieldIds();

          buildFieldDragImageFromState({
            e,
            selectedFieldIds,
            thRef: thRef as React.RefObject<HTMLTableCellElement>,
            tableBodyRef: (ref as TableLayoutHeaderCellRefObj)?.current
              ?.tableBodyRef as React.RefObject<HTMLTableSectionElement | null>,
            dragImageRef: dragImage
          });

          setPublicDragData(e.dataTransfer, {
            type: DragType.FIELD_COLUMNS,
            contentId,
            draggingIds: selectedFieldIds,
            dragOverId: fieldId,
            height: headerCellElement.offsetHeight,
            width: headerCellElement.offsetWidth
          });

          onDragStart?.(e, fieldId);
        },
        [closeMenu, contentId, fieldId, onDragStart, ref, selectionsReader]
      );

      const handleDrag = useCallback(
        (e: React.DragEvent) => {
          if (e.target !== e.currentTarget) {
            // Ignore dragging descendent elements
            return;
          }

          onDrag?.(e, fieldId);
        },
        [fieldId, onDrag]
      );

      const handleDragEnd = useCallback(
        (e: React.DragEvent) => {
          if (e.target !== e.currentTarget) {
            // Ignore dragging descendent elements
            return;
          }

          if (dragImage.current) {
            dragImage.current.remove();
            dragImage.current = undefined;
          }

          onDragEnd?.(e, fieldId);
        },
        [fieldId, onDragEnd]
      );

      // Pragmatic drag and drop
      useEffect(() => {
        if (!thRef.current) {
          return;
        }
        const element = thRef.current;
        const boundaryDropTargetElement = pinnedLeadingBoundaryDropTargetRef.current;

        const registrations = [
          draggable({
            element,
            getInitialData() {
              return { type: DragType.FIELD_COLUMNS, fieldIndex, fieldId };
            },
            onDragStart() {
              setDragState({ type: 'dragging' });
            },
            onDrop() {
              setDragState({ type: 'idle' });
            }
          }),
          dropTargetForElements({
            element,
            getData({ input, element }) {
              const data = { fieldId, fieldIndex };
              return attachClosestEdge(data, {
                input,
                element,
                allowedEdges: ['left', 'right']
              });
            },
            canDrop({ source }) {
              return (
                (source.data.type === DragType.FIELD_COLUMNS &&
                  source.data.fieldId !== fieldId) ||
                source.data.type === DragType.FILL_VALUES
              );
            },
            onDragEnter(args) {
              if (args.source.data.type === DragType.FIELD_COLUMNS) {
                setDragState({
                  type: 'is-over',
                  closestEdge: extractClosestEdge(args.self.data)
                });
              }
            },
            onDrag(args) {
              if (args.source.data.type === DragType.FIELD_COLUMNS) {
                const closestEdge: Edge | null = extractClosestEdge(args.self.data);

                // only update react state if the `closestEdge` changes
                setDragState((current) => {
                  if (current.type !== 'is-over') {
                    return current;
                  }
                  if (current.closestEdge === closestEdge) {
                    return current;
                  }
                  return {
                    type: 'is-over',
                    closestEdge
                  };
                });
              }
            },
            onDragLeave() {
              setDragState({ type: 'idle' });
            },
            onDrop() {
              setDragState({ type: 'idle' });
            }
          })
        ];

        if (isPinnedLeadingFreezeBoundary && boundaryDropTargetElement) {
          registrations.push(
            dropTargetForElements({
              element: boundaryDropTargetElement,
              getData({ input, element }) {
                const data = { fieldId, fieldIndex };
                return attachClosestEdge(data, {
                  input,
                  element,
                  allowedEdges: ['right']
                });
              },
              canDrop({ source }) {
                return (
                  (source.data.type === DragType.FIELD_COLUMNS &&
                    source.data.fieldId !== fieldId) ||
                  source.data.type === DragType.FILL_VALUES
                );
              },
              onDragEnter(args) {
                if (args.source.data.type === DragType.FIELD_COLUMNS) {
                  setDragState({
                    type: 'is-over',
                    closestEdge: extractClosestEdge(args.self.data)
                  });
                }
              },
              onDrag(args) {
                if (args.source.data.type === DragType.FIELD_COLUMNS) {
                  const closestEdge: Edge | null = extractClosestEdge(args.self.data);

                  setDragState((current) => {
                    if (current.type !== 'is-over') {
                      return current;
                    }
                    if (current.closestEdge === closestEdge) {
                      return current;
                    }
                    return {
                      type: 'is-over',
                      closestEdge
                    };
                  });
                }
              },
              onDragLeave() {
                setDragState({ type: 'idle' });
              },
              onDrop() {
                setDragState({ type: 'idle' });
              }
            })
          );
        }

        return combine(...registrations);
      }, [fieldIndex, fieldId, isPinnedLeadingFreezeBoundary]);

      const shouldShowTelepointer = !isSelected;
      const nameplateRef = useRef<HTMLTableCellElement>(null);
      const [isHovering, setIsHovering] = useState(false);
      const handleMouseEnter = useCallback(() => {
        if (!shouldShowTelepointer) {
          return;
        }
        setIsHovering(true);
      }, [shouldShowTelepointer]);

      const handleMouseLeave = useCallback(() => {
        if (!shouldShowTelepointer) {
          return;
        }
        setIsHovering(false);
      }, [shouldShowTelepointer]);

      const pinnedLeadingStyle = useMemo((): CSSProperties | undefined => {
        return getPinnedLeadingHeaderCellStyle({
          isEnabled: isPinnedLeadingEnabled,
          layout: pinnedLeadingLayout,
          isSelectedFromPing: isFieldSelectedFromPing,
          isFieldDragActive: isDraggingActive ?? false,
          maxPinnedLeadingFieldCount
        });
      }, [
        isFieldSelectedFromPing,
        isPinnedLeadingEnabled,
        isDraggingActive,
        maxPinnedLeadingFieldCount,
        pinnedLeadingLayout
      ]);

      const pinnedLeadingMenuConfig = useMemo(() => {
        if (!isPinnedLeadingEnabled) {
          return undefined;
        }
        return {
          fieldIndex,
          pinnedLeadingFieldCount,
          maxPinnedLeadingFieldCount,
          onPinThroughThisColumn: () => setPinnedLeadingFieldCount(fieldIndex + 1),
          onUnpinAllColumns: () => setPinnedLeadingFieldCount(0),
          onFieldsInserted: ({
            insertBefore,
            selectedFieldIds
          }: {
            insertBefore: boolean;
            selectedFieldIds: ReadonlyArray<string>;
          }) => {
            if (selectedFieldIds.length === 0) {
              return;
            }
            const anchorFieldId = insertBefore
              ? selectedFieldIds[0]
              : selectedFieldIds[selectedFieldIds.length - 1];
            if (!anchorFieldId) {
              return;
            }
            const anchorIndex = visibleFieldIds.indexOf(anchorFieldId);
            if (anchorIndex < 0) {
              return;
            }
            const insertionIndex = insertBefore ? anchorIndex : anchorIndex + 1;
            const nextPinnedLeadingFieldCount = Math.min(
              getPinnedLeadingFieldCountAfterFieldsInserted({
                fieldIds: visibleFieldIds,
                insertionIndex,
                pinnedLeadingFieldCount
              }),
              maxPinnedLeadingFieldCount
            );

            if (nextPinnedLeadingFieldCount !== pinnedLeadingFieldCount) {
              setPinnedLeadingFieldCount(nextPinnedLeadingFieldCount);
            }
          },
          onFieldsRemoved: (fieldIdsToRemove: ReadonlyArray<string>) => {
            const nextPinnedLeadingFieldCount =
              getPinnedLeadingFieldCountAfterFieldsRemoved({
                fieldIds: visibleFieldIds,
                removedFieldIds: fieldIdsToRemove,
                pinnedLeadingFieldCount
              });

            if (nextPinnedLeadingFieldCount !== pinnedLeadingFieldCount) {
              setPinnedLeadingFieldCount(nextPinnedLeadingFieldCount);
            }
          },
          getViewChangesForFieldsHidden: (
            fieldIdsToHide: ReadonlyArray<string>
          ): ViewChangeType[] => {
            const nextPinnedLeadingFieldCount =
              getPinnedLeadingFieldCountAfterFieldsRemoved({
                fieldIds: visibleFieldIds,
                removedFieldIds: fieldIdsToHide,
                pinnedLeadingFieldCount
              });

            if (nextPinnedLeadingFieldCount !== pinnedLeadingFieldCount) {
              return [
                {
                  type: ViewChangeTypes.VIEW_PROPERTIES,
                  value: setPinnedLeadingFieldCountInProperties(
                    view.settings.properties,
                    nextPinnedLeadingFieldCount
                  )
                }
              ];
            }

            return [];
          }
        };
      }, [
        fieldIndex,
        isPinnedLeadingEnabled,
        maxPinnedLeadingFieldCount,
        pinnedLeadingFieldCount,
        setPinnedLeadingFieldCount,
        view.settings.properties,
        visibleFieldIds
      ]);

      const isFieldFrozen = fieldIndex < pinnedLeadingFieldCount;
      const canShowFreezeColumnSpotlight =
        isPinnedLeadingEnabled &&
        (isFieldFrozen || fieldIndex + 1 <= maxPinnedLeadingFieldCount);
      const shouldShowFieldMenuSpotlightTour =
        isContextualMenusM2Enabled &&
        canEditFields &&
        isFieldSelectedFromPing &&
        selectionsApi.getSelectedFieldIds().length === 1 &&
        (!isPinnedLeadingEnabled || canShowFreezeColumnSpotlight);

      const openFieldMenuForSpotlightTour = useCallback(() => {
        fieldMenuRef.current?.open();
      }, []);
      const advanceFieldMenuSpotlightTour = useCallback(() => {
        setFieldMenuOpenEvent((event) => ({
          id: (event?.id ?? 0) + 1,
          source: 'drag-handle'
        }));
      }, []);

      const isPinnedLeadingCell = pinnedLeadingStyle !== undefined;
      const isFirstScrollableAfterPinnedLeading =
        isPinnedLeadingEnabled &&
        pinnedLeadingFieldCount > 0 &&
        fieldIndex === pinnedLeadingFieldCount;
      const pinnedLeadingGapCoverStyle =
        isPinnedLeadingCell && fieldIndex > 0
          ? getPinnedLeadingHeaderSurfaceGapCoverStyle()
          : undefined;
      const pinnedLeadingBoundaryShadowStyle =
        isPinnedLeadingFreezeBoundary && visibleTableContentHeight > 0
          ? getPinnedLeadingBoundaryCellShadowStyle()
          : undefined;
      const shouldShowDropIndicator =
        dragState.type === 'is-over' &&
        Boolean(dragState.closestEdge) &&
        !isPinnedLeadingDropIndicatorOccluded({
          cellElement: thRef.current,
          boundaryElement: pinnedLeadingBoundaryElementRef.current,
          closestEdge: dragState.closestEdge,
          isPinnedLeadingEnabled,
          isPinnedLeadingCell,
          isPinnedLeadingFreezeBoundary,
          isFirstScrollableAfterPinnedLeading
        });
      return (
        <Th
          id={fieldId}
          containerHeight={containerHeight}
          isDragOver={isDragOver}
          draggable={canEditFields && isHoverDragButton}
          onDragStart={handleDragStart}
          onDrag={handleDrag}
          onDragEnd={handleDragEnd}
          isFirstInRow={isFirstInRow}
          data-testid="table-layout-header-cell"
          data-field-id={fieldId}
          data-col-index={fieldIndex}
          ref={composeRefs(
            thRef,
            (ref as TableLayoutHeaderCellRefObj)?.current?.headerFieldRef,
            nameplateRef
          )}
          className={classNames([
            className,
            {
              [TABLE_CLASSNAMES.MAXIMIZE_HEIGHT]: isDraggingActive,
              [TABLE_CLASSNAMES.SHOW_AS_DISABLED]: showAsDisabled
            }
          ])}
          isSelectedFromPing={isFieldSelectedFromPing}
          isFirstOfSelection={isFieldFirstOfSelection}
          isLastOfSelection={isFieldLastOfSelection}
          collaboratorColor={recentCollaboratorColor}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClickCapture={handleHeaderClickCapture}
          onContextMenuCapture={handleHeaderContextMenu}
          style={
            isPinnedLeadingEnabled
              ? { zIndex: 0, ...pinnedLeadingStyle }
              : pinnedLeadingStyle
          }
          data-dbs-pinned-leading={pinnedLeadingStyle ? 'true' : undefined}
          data-dbs-pinned-leading-freeze-boundary={
            isPinnedLeadingFreezeBoundary ? 'true' : undefined
          }
        >
          {pinnedLeadingGapCoverStyle && (
            <div
              data-testid="pinned-leading-surface-gap-cover"
              style={pinnedLeadingGapCoverStyle}
            />
          )}
          {pinnedLeadingBoundaryShadowStyle && (
            <div
              data-testid="pinned-leading-freeze-boundary-shadow"
              style={pinnedLeadingBoundaryShadowStyle}
            />
          )}
          {isPinnedLeadingFreezeBoundary && (
            <div
              ref={pinnedLeadingBoundaryDropTargetRef}
              data-testid="pinned-leading-freeze-boundary-drop-target"
              style={
                isDraggingActive
                  ? PINNED_LEADING_BOUNDARY_DROP_TARGET_ACTIVE_STYLE
                  : PINNED_LEADING_BOUNDARY_DROP_TARGET_INACTIVE_STYLE
              }
            />
          )}
          {(isHovering || activeBackendAgentParticipant) &&
            shouldShowTelepointer &&
            !isAIStreaming &&
            displayParticipants.length > 0 && (
              <PresenceNameplate
                participants={displayParticipants}
                anchorRef={nameplateRef}
                appearance={activeBackendAgentParticipant ? 'ai-agent' : 'default'}
                position={activeBackendAgentParticipant ? 'top' : 'bottom'}
              />
            )}
          <FieldMenuSpotlightTourProvider
            closeFieldMenu={closeMenu}
            databaseViewportAnchorRef={thRef}
            fieldMenuOpenEvent={fieldMenuOpenEvent}
            isEnabled={shouldShowFieldMenuSpotlightTour}
            isFreezeColumnStepEnabled={canShowFreezeColumnSpotlight}
            openFieldMenu={openFieldMenuForSpotlightTour}
          >
            {canEditFields && dragState.type === 'idle' && (
              <SelectFieldMenu
                ref={fieldMenuRef}
                contentId={contentId}
                viewSettings={viewSettings}
                currentViewId={currentViewId}
                canEditFields={canEditFields}
                fieldId={fieldId}
                hoverProps={dragButtonHoverProps}
                handleSelectColumn={handleSelectColumn}
                hasUserEditedAiSummaryCells={getHasUserEditedAiSummaryCells}
                onOpenFromDragHandle={advanceFieldMenuSpotlightTour}
                onAiSummaryRerunEntireColumn={handleAiSummaryRerunEntireColumn}
                onAiSummaryRerunEmptyEntries={handleAiSummaryRerunEmptyEntries}
                isAiSummaryRerunDisabled={isAiFieldRunActive || isLocalAiSummaryRunActive}
                pinnedLeadingMenu={pinnedLeadingMenuConfig}
                onEditAiSummaryField={handleEditAiSummaryField}
              />
            )}
            <FieldMenuSpotlightTarget
              step="field-header"
              placement="right-end"
              offset={[0, 8]}
              targetRef={thRef}
            />
            <FieldNameWithActions
              contentId={contentId}
              fieldId={fieldId}
              isReadonly={!canEditFields}
              isDragging={showAsDisabled}
              isSelected={false}
              isSoftSelected={false}
              hoverDeleteButton={false}
              isLastInRow={isLastInRow}
              ref={inputRef}
              isFirstInRow
              onFieldSettingsPanelOpen={fireContextualMenusM2Exposure}
              openSettingsPanelOnClick={!isContextualMenusM2Enabled}
              fieldActionComp={
                canEditFields && aiSummaryField ? (
                  <AiSummaryFieldHeaderAction
                    confirmationKey={`${contentId}:${fieldId}`}
                    hasUserEditedCells={getHasUserEditedAiSummaryCells}
                    lastRunAt={aiFieldLastRunAt}
                    onRerunEntireColumn={handleAiSummaryRerunEntireColumn}
                    onRerunEmptyEntries={handleAiSummaryRerunEmptyEntries}
                    isDisabled={isAiFieldRunActive || isLocalAiSummaryRunActive}
                  />
                ) : undefined
              }
              {...otherProps}
            />
          </FieldMenuSpotlightTourProvider>
          {shouldShowDropIndicator &&
            dragState.type === 'is-over' &&
            dragState.closestEdge &&
            (dragState.closestEdge === 'right' && isPinnedLeadingFreezeBoundary ? (
              <PragmaticDropIndicatorStylingLastInRow containerHeight={containerHeight} />
            ) : isLastInRow && dragState.closestEdge === 'right' ? (
              <PragmaticDropIndicatorStylingLastInRow containerHeight={containerHeight}>
                <DropIndicator edge={dragState.closestEdge} />
              </PragmaticDropIndicatorStylingLastInRow>
            ) : (
              <PragmaticDropIndicatorStyling containerHeight={containerHeight}>
                <DropIndicator edge={dragState.closestEdge} />
              </PragmaticDropIndicatorStyling>
            ))}
        </Th>
      );
    }
  )
);

export default TableLayoutHeaderCell;
